import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../../utils/api";
import DialogueCard from "../../../../modules/onboarding/components/DialogueCard";
import { Navigate, useNavigate, createFileRoute } from "@tanstack/react-router";
import { snakeCaseToSentenceCase } from "../../../../utils/formatText";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { completeAmzProfileSelection } from "../../../../redux/reducers/amzOnboardReducer";
import { setCurrentUser } from "../../../../redux/reducers/userReducer";
import { useAppDispatch } from "../../../../redux/hooks";
import { SearchInput } from "../../../../components/fields/base/TextField";
import { css } from "../../../../../styled-system/css";
import { VStack } from "../../../../../styled-system/jsx";
import SignupLoading from "../../../../modules/onboarding/components/SignupLoading";
import { useAdvertisingProfileOptions } from "../../../../modules/onboarding/amazon/useAdvertisingProfileOptions";
import { useCurrentUserProfile } from "../../../../modules/auth/api/useCurrentUserProfile";

const Profiles = () => {
  const { formattedProfiles: profiles } = useAdvertisingProfileOptions();
  const { data: userProfile } = useCurrentUserProfile();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["onboarding"]);

  const queryClient = useQueryClient();

  const savePrimaryProfile = useMutation({
    mutationFn: async (primaryProfileData) => {
      return await api.post("/api/accounts/", primaryProfileData);
    },
    onSuccess: async (res, variables) => {
      dispatch(completeAmzProfileSelection());

      // Keep redux state up to date with fields updated in backend for initial visit to the dashboard and providing right redirects for SP-API auth based on org type
      dispatch(
        setCurrentUser({
          current_profile: variables.selected_profile,
          organization_type: variables.type,
        }),
      );
      await queryClient.invalidateQueries({
        queryKey: ["currentUserProfile"],
      });

      navigate({ to: "/onboarding/v2/connect" });
    },
    onError: async (error, variables) => {
      // If user loses tokens, send back to sign in page
      if (error.response.status === 401) {
        navigate({ to: "/" });
      }

      // If error assigning primary profile which can be caused by race condition, try switching user to already existing profile
      // to prevent them from getting stuck on the profile selection screen
      if (error.response.status === 500) {
        const destinationAccountData = {
          account: variables.selected_profile,
        };

        if (variables.type === "vendor") {
          destinationAccountData["vendor"] = true;
        }
        const res = await api.put("/api/accounts/", destinationAccountData);

        if (res.status === 204) {
          dispatch(
            setCurrentUser({
              current_profile: variables.selected_profile,
              organization_type: variables.type,
            }),
          );
          navigate({ to: "/onboarding/v2/connect", replace: true });
        }
      }
    },
  });

  const filteredProfiles = useMemo(() => {
    if (search) {
      return profiles?.filter((profile) => profile.label.toLowerCase()?.includes(search?.toLowerCase()));
    }

    return profiles;
  }, [profiles, search]);

  // Once a profile is bound to the org it can't be changed, so block re-selection
  if (userProfile?.current_profile) return <Navigate to="/onboarding/v2/connect" replace />;

  return (
    <VStack gap="large" width="100%" paddingY="xlarge" maxHeight="72vh" overflowY="auto">
      {savePrimaryProfile.isPending ?
        <VStack
          gap="small"
          alignItems="stretch"
          width="100%"
          maxWidth="600px"
          marginX="auto"
          paddingX="medium"
          minHeight="400px"
        >
          <h2 className={css({ fontWeight: "semibold" })}>{t(($) => $.onboarding["Amazon Advertising API"])}</h2>
          <SignupLoading />
        </VStack>
      : <>
          <VStack gap="small" alignItems="stretch" width="100%" maxWidth="600px" marginX="auto" paddingX="medium">
            <h2 className={css({ fontWeight: "semibold" })}>{t(($) => $.onboarding["Amazon Advertising API"])}</h2>
            <p className={css({ fontSize: "large" })}>
              {t(($) => $.onboarding["We require access to both the SP-API and Amazon Advertising API"])}
            </p>
          </VStack>
          <VStack gap="medium" alignItems="stretch" width="100%" maxWidth="600px" marginX="auto" paddingX="medium">
            <h4 className={css({ fontWeight: "medium" })}>{t(($) => $.onboarding["Select Your Default Profile"])}</h4>
            <SearchInput
              value={search}
              onChange={(value) => setSearch(value)}
              placeholder={t(($) => $.onboarding["Search for a profile..."])}
            />
            {filteredProfiles.map((profile, index) => {
              return (
                <DialogueCard
                  key={`${profile.value}-${profile.type}-${index}`}
                  title={profile.label}
                  description={`${snakeCaseToSentenceCase(profile.type)} (${profile.marketPlaces?.join(", ")})`}
                  justify="center"
                  onPress={() => {
                    const primaryProfileData = {
                      selected_profile: profile.value,
                      type: profile.type,
                    };

                    if (profile.type === "vendor") {
                      primaryProfileData["marketplace"] = profile.marketPlaces[0];
                      primaryProfileData["profile_id"] = profile.profileId;
                    }

                    savePrimaryProfile.mutate(primaryProfileData);
                  }}
                />
              );
            })}
            <p className={css({ fontSize: "medium", textAlign: "center" })}>
              {t(
                ($) =>
                  $.onboarding[
                    "Not seeing a profile you have access to? Please contact Customer Success as it may have already been connected to Trellis."
                  ],
              )}
            </p>
          </VStack>
        </>
      }
    </VStack>
  );
};

export const Route = createFileRoute("/_authenticated/onboarding/v2/profiles")({
  component: Profiles,
});
